import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { api, formatApiError } from "../lib/api";
import { Field } from "./Register";

export default function ResetPassword() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError(lang === "en" ? "Use 6 characters or more." : "Folosește 6 caractere sau mai multe.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate(lp("/login")), 1500);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-16 md:py-24 max-w-md">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="glass rounded-3xl p-8 md:p-10"
      >
        {done ? (
          <>
            <CheckCircle2 className="h-9 w-9 text-gold" />
            <h1 className="mt-6 font-display text-h1 tracking-tight">
              {lang === "en" ? "Password updated." : "Parolă actualizată."}
            </h1>
            <p className="mt-3 text-cream-muted">
              {lang === "en" ? "Sign in with your new password." : "Autentifică-te cu noua parolă."}
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display text-card-lg md:text-section tracking-tight leading-[1.05]">{t.auth.resetTitle}</h1>
            <p className="mt-3 text-cream-muted">{t.auth.resetSub}</p>
            <form onSubmit={onSubmit} className="mt-10 space-y-5">
              <Field icon={<Lock className="h-4 w-4 text-cream-dim" />} label={t.auth.newPassword}>
                <input
                  required
                  minLength={6}
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.auth.newPassword}
                  className="input-bare"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="text-cream-dim hover:text-cream">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </Field>
              {error && <p className="text-caption text-red-300/90">{error}</p>}
              <button type="submit" disabled={loading} className="btn btn-gold btn-lg w-full">
                {loading ? t.common.sending : t.auth.submitReset}
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
